// src/components/checklists/NewJobChecklistsSection.js
// CHECKLISTS SEAM 6c VERIFIER: chklstnew-5a8c2e71
//
// Checklists on the NEW-job form (JobFormModal, web). A new job has no id yet, so nothing is
// attached here: the person only CHOOSES. JobFormModal saves the job, then calls
// attachChecklistsToNewJob (checklistClient.js) for each pick — one attachChecklistToJob call
// each, through the same server checks as the pop-up. Nothing chosen here goes into the job's
// own save payload.
//
//   "Added automatically"  every non-archived "every new job" template, read-only — the server
//                          trigger (autoAttachChecklistsOnJobCreate) adds these on create.
//   "+ Add checklist"      the pop-up's picker: non-archived, not already chosen or automatic.
//
// Display only. Shown by JobFormModal to office roles on a new one-off Job, and only when the
// company has at least one non-archived template — hidden entirely while loading, on a failed
// read, or with none, so companies without checklists never see an empty box. Independent of
// the builder roll-out switch.

import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { ClipboardCheck, Plus, X } from 'lucide-react';
import { db } from '../../firebase/config';

const RequiredTag = () => (
    <span className="text-[10px] font-semibold text-[#5F4EC9]">Required to complete</span>
);

// selected: [{ id, name, blocksCompletion }] — owned by JobFormModal. onChange(nextSelected).
const NewJobChecklistsSection = ({ orgId, selected, onChange, disabled }) => {
    const [templates, setTemplates] = useState(null); // null = not loaded
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (!orgId) return undefined;
        let cancelled = false;
        getDocs(collection(db, `organizations/${orgId}/checklistTemplates`))
            .then((snap) => { if (!cancelled) setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() }))); })
            // A failed read hides the section (we can't tell whether the company has checklists);
            // the job saves as normal and checklists can still be added from the saved job.
            .catch((err) => console.error('NewJobChecklistsSection: could not load checklists', err));
        return () => { cancelled = true; };
    }, [orgId]);

    const live = (templates || []).filter(t => t.archived !== true);
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
    const automatic = live.filter(t => t.autoAttach === 'every_job').sort(byName);
    const chosenIds = new Set(selected.map(t => t.id));
    const addable = live.filter(t => t.autoAttach !== 'every_job' && !chosenIds.has(t.id)).sort(byName);

    const pick = (t) => {
        onChange([...selected, { id: t.id, name: t.name || 'Checklist', blocksCompletion: t.blocksCompletion === true }]);
        setMenuOpen(false);
    };
    const unpick = (id) => onChange(selected.filter(t => t.id !== id));

    // Only for a company that HAS checklists: nothing at all while loading (no empty box
    // flickering in), when the read fails, or when every template is archived / there are none.
    // Deliberately NOT tied to the builder roll-out switch.
    if (templates === null || live.length === 0) return null;

    return (
        <div className="rounded-lg border border-slate-100 overflow-hidden">
            <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-100 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                    <ClipboardCheck size={11} /> Checklists
                </p>
                {!menuOpen && (
                    <button type="button" onClick={() => setMenuOpen(true)} disabled={disabled}
                        className="flex items-center gap-1 text-xs font-semibold text-[#5F4EC9] hover:text-[#4f3fb5] disabled:opacity-40">
                        <Plus size={12} /> Add checklist
                    </button>
                )}
            </div>

            <div className="p-3 space-y-2">

                {/* Inline, never floating: the form's scroll area would clip it. */}
                {menuOpen && (
                    <div className="rounded-lg border border-[#5F4EC9]/30 bg-white">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                            <p className="text-xs font-semibold text-slate-600">Add a checklist</p>
                            <button type="button" onClick={() => setMenuOpen(false)}
                                className="text-xs font-semibold text-slate-500 hover:text-slate-800">
                                Cancel
                            </button>
                        </div>
                        <div className="py-1">
                            {addable.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No other checklists to add.</p>}
                            {addable.map(t => (
                                <button key={t.id} type="button" onClick={() => pick(t)} disabled={disabled}
                                    className="w-full text-left px-3 py-2 hover:bg-slate-50 disabled:opacity-40">
                                    <span className="block text-sm text-slate-700">{t.name}</span>
                                    {t.blocksCompletion && <RequiredTag />}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {automatic.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold text-slate-500 mb-1">Added automatically</p>
                        <div className="flex flex-wrap gap-1.5">
                            {automatic.map(t => (
                                <span key={t.id} className="inline-flex flex-col px-2.5 py-1 rounded-lg border border-slate-200 bg-slate-50">
                                    <span className="text-xs font-medium text-slate-700">{t.name}</span>
                                    <span className="text-[10px] text-slate-400">Added to every new job</span>
                                    {t.blocksCompletion && <RequiredTag />}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {selected.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold text-slate-500 mb-1">Added to this job when you save</p>
                        <div className="flex flex-wrap gap-1.5">
                            {selected.map(t => (
                                <span key={t.id} className="inline-flex items-start gap-1.5 pl-2.5 pr-1 py-1 rounded-lg border border-[#5F4EC9]/30 bg-[#5F4EC9]/5">
                                    <span className="flex flex-col">
                                        <span className="text-xs font-medium text-slate-700">{t.name}</span>
                                        {t.blocksCompletion && <RequiredTag />}
                                    </span>
                                    <button type="button" onClick={() => unpick(t.id)} disabled={disabled}
                                        title="Don't add this checklist" aria-label={`Don't add ${t.name}`}
                                        className="p-0.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40">
                                        <X size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {templates !== null && automatic.length === 0 && selected.length === 0 && !menuOpen && (
                    <p className="text-xs text-slate-400">No checklists on this job. Use "Add checklist" to add one.</p>
                )}
            </div>
        </div>
    );
};

export default NewJobChecklistsSection;
